import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Interfaces for Storage API
export interface UploadResponse {
  message: string;
  object_key: string;
  url: string;
}

export interface FileInfo {
  object_key: string;
  size: number;
  last_modified: string;
  tags?: Record<string, string>;
}

export interface FileListResponse {
  files: FileInfo[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface PresignedUrlResponse {
  url: string;
  object_key: string;
  expires_in: number;
}

export interface BulkPresignedUrlRequest {
  object_keys: string[];
  expires_in?: number;
}

export interface BulkPresignedUrlResponse {
  urls: Array<{
    object_key: string;
    url: string;
  }>;
}

export interface TagsResponse {
  object_key: string;
  tags: Record<string, string>;
}

export interface FileEntityLink {
  entity_type: string;
  entity_id: string;
  link_type?: string;
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private baseUrl = `${environment.fastApiUrl}/storage`;

  constructor(private http: HttpClient) {}

  private getHeaders(companyId: string, token: string): HttpHeaders {
    return new HttpHeaders({
      'Authorization': token,
      'x-company-id': companyId,
      'Accept': '*/*'
    });
  }

  /**
   * Upload a file to storage
   * @param file File to upload
   * @param objectKey S3 object key/path for the file
   * @param companyId Company ID
   * @param token Authorization token
   * @param tags Optional tags as key:value pairs (e.g., 'patient:123,session:456')
   */
  uploadFile(
    file: File,
    objectKey: string,
    companyId: string,
    token: string,
    tags?: string,
    links?: FileEntityLink[]
  ): Observable<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('object_key', objectKey);
    if (tags) {
      formData.append('tags', tags);
    }
    if (links?.length) {
      formData.append('links_json', JSON.stringify(links));
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'X-Company-ID': companyId
    });
    
    return this.http.post<UploadResponse>(`${this.baseUrl}/upload`, formData, { headers });
  }

  /**
   * Download a file from storage
   * @param objectKey File object key
   * @param companyId Company ID
   * @param token Authorization token
   */
  downloadFile(objectKey: string, companyId: string, token: string): Observable<Blob> {
    const headers = this.getHeaders(companyId, token);
    return this.http.get(`${this.baseUrl}/download/${encodeURIComponent(objectKey)}`, {
      headers,
      responseType: 'blob'
    });
  }

  /**
   * List files in storage
   * @param companyId Company ID
   * @param token Authorization token
   * @param prefix Optional prefix filter
   * @param recursive Whether to list recursively (default: true)
   * @param tags Optional tags filter (e.g., 'patient:123,session:456')
   */
  listFiles(
    companyId: string,
    token: string,
    prefix: string = '',
    recursive: boolean = true,
    tags?: string
  ): Observable<any> {
    const headers = this.getHeaders(companyId, token);
    let params = new HttpParams()
      .set('prefix', prefix)
      .set('recursive', recursive.toString());
    
    if (tags) {
      params = params.set('tags', tags);
    }

    return this.http.get(`${this.baseUrl}/files`, { headers, params });
  }

  listFilesByEntity(
    entityType: string,
    entityId: string,
    companyId: string,
    token: string,
    includeRelatedComments: boolean = false,
    fallbackTagScan: boolean = true
  ): Observable<any> {
    const headers = this.getHeaders(companyId, token);
    const params = new HttpParams()
      .set('entity_type', entityType)
      .set('entity_id', entityId)
      .set('include_related_comments', includeRelatedComments.toString())
      .set('fallback_tag_scan', fallbackTagScan.toString());
    return this.http.get(`${this.baseUrl}/files/by-entity`, { headers, params });
  }

  /**
   * Delete a file from storage
   * @param objectKey File object key
   * @param companyId Company ID
   * @param token Authorization token
   */
  deleteFile(objectKey: string, companyId: string, token: string): Observable<any> {
    const headers = this.getHeaders(companyId, token);
    return this.http.delete(`${this.baseUrl}/files/${encodeURIComponent(objectKey)}`, { headers });
  }

  /**
   * Generate presigned URL for download
   * @param objectKey Object key
   * @param companyId Company ID
   * @param token Authorization token
   * @param expiresIn Expiration time in seconds (default: 3600)
   */
  getPresignedDownloadUrl(
    objectKey: string,
    companyId: string,
    token: string,
    expiresIn: number = 3600
  ): Observable<PresignedUrlResponse> {
    const headers = this.getHeaders(companyId, token);
    const params = new HttpParams().set('expires_in', expiresIn.toString());

    return this.http.get<PresignedUrlResponse>(
      `${this.baseUrl}/presigned-url/download/${encodeURIComponent(objectKey)}`, 
      { headers, params }
    );
  }

  /**
   * Generate presigned URL for upload
   * @param objectKey Destination object key
   * @param companyId Company ID
   * @param token Authorization token
   * @param expiresIn Expiration time in seconds (default: 3600)
   */
  getPresignedUploadUrl(
    objectKey: string,
    companyId: string,
    token: string,
    expiresIn: number = 3600
  ): Observable<PresignedUrlResponse> {
    const headers = this.getHeaders(companyId, token);
    const params = new HttpParams().set('expires_in', expiresIn.toString());

    return this.http.get<PresignedUrlResponse>(
      `${this.baseUrl}/presigned-url/upload/${encodeURIComponent(objectKey)}`, 
      { headers, params }
    );
  }

  /**
   * Generate bulk presigned URLs for download
   * @param request Bulk request with object keys
   * @param companyId Company ID
   * @param token Authorization token
   */
  getBulkPresignedDownloadUrls(
    request: BulkPresignedUrlRequest,
    companyId: string,
    token: string
  ): Observable<BulkPresignedUrlResponse> {
    const headers = this.getHeaders(companyId, token);
    return this.http.post<BulkPresignedUrlResponse>(
      `${this.baseUrl}/bulk/presigned-urls/download`, 
      request, 
      { headers }
    );
  }

  /**
   * Generate bulk presigned URLs for upload
   * @param request Bulk request with object keys
   * @param companyId Company ID
   * @param token Authorization token
   */
  getBulkPresignedUploadUrls(
    request: BulkPresignedUrlRequest,
    companyId: string,
    token: string
  ): Observable<BulkPresignedUrlResponse> {
    const headers = this.getHeaders(companyId, token);
    return this.http.post<BulkPresignedUrlResponse>(
      `${this.baseUrl}/bulk/presigned-urls/upload`, 
      request, 
      { headers }
    );
  }

  /**
   * Get tags for a file
   * @param objectKey Object key
   * @param companyId Company ID
   * @param token Authorization token
   */
  getFileTags(objectKey: string, companyId: string, token: string): Observable<TagsResponse> {
    const headers = this.getHeaders(companyId, token);
    return this.http.get<TagsResponse>(
      `${this.baseUrl}/files/${encodeURIComponent(objectKey)}/tags`, 
      { headers }
    );
  }

  /**
   * Set tags for a file (replaces existing tags)
   * @param objectKey Object key
   * @param tags Tags to set
   * @param companyId Company ID
   * @param token Authorization token
   */
  setFileTags(
    objectKey: string,
    tags: Record<string, string>,
    companyId: string,
    token: string
  ): Observable<TagsResponse> {
    const headers = this.getHeaders(companyId, token);
    return this.http.put<TagsResponse>(
      `${this.baseUrl}/files/${encodeURIComponent(objectKey)}/tags`, 
      { tags }, 
      { headers }
    );
  }

  /**
   * Delete tags from a file
   * @param objectKey Object key
   * @param companyId Company ID
   * @param token Authorization token
   */
  deleteFileTags(objectKey: string, companyId: string, token: string): Observable<any> {
    const headers = this.getHeaders(companyId, token);
    return this.http.delete(
      `${this.baseUrl}/files/${encodeURIComponent(objectKey)}/tags`, 
      { headers }
    );
  }

  /**
   * Check bucket status
   * @param companyId Company ID
   * @param token Authorization token
   */
  checkBucketStatus(companyId: string, token: string): Observable<any> {
    const headers = this.getHeaders(companyId, token);
    return this.http.get(`${this.baseUrl}/bucket`, { headers });
  }

  /**
   * Get valid entity types for file tagging
   * @param companyId Company ID
   * @param token Authorization token
   */
  getEntityTypes(companyId: string, token: string): Observable<any> {
    const headers = this.getHeaders(companyId, token);
    return this.http.get(`${this.baseUrl}/entity-types`, { headers });
  }
}
